<script setup>
import { Head, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'

defineOptions({ layout: PublicLayout })

const props = defineProps({
  vehicles:   { type: Array,  default: () => [] },
  categories: { type: Array,  default: () => [] },
  filters:    { type: Object, default: () => ({}) },
  base_path:  { type: String, default: '/rentals' }
})

const { t } = useI18n()

function setFilter(key, value) {
  const next = { ...props.filters, [key]: value || undefined }
  Object.keys(next).forEach(k => { if (!next[k]) delete next[k] })
  router.get(props.base_path, next, { preserveScroll: true, preserveState: false })
}

// Render the small meta line under the title. Bikes show engine cc;
// cars show seats. Both show transmission and fuel when present.
function cardMeta(v) {
  const parts = []
  if (v.vehicle_class === 'two_wheeler') {
    if (v.engine_cc) parts.push(`${v.engine_cc}cc`)
  } else {
    if (v.seats) parts.push(t('public.catalog.seats_count', { n: v.seats }))
  }
  if (v.transmission) parts.push(t(`public.transmissions.${v.transmission}`))
  if (v.fuel_type)    parts.push(t(`public.fuels.${v.fuel_type}`))
  return parts.join(' · ')
}
</script>

<template>
  <Head>
    <title>{{ t('public.catalog.head_title') }}</title>
    <meta name="description" :content="t('public.catalog.head_description')" />
  </Head>

  <section class="catalog">
    <header class="catalog__header">
      <h1 class="catalog__title">{{ t('public.catalog.title') }}</h1>
      <p class="catalog__lede">{{ t('public.catalog.lede') }}</p>
    </header>

    <div class="catalog__toolbar">
      <div class="chips">
        <button
          :class="['chip', !filters.category && 'is-active']"
          @click="setFilter('category', null)"
        >{{ t('public.catalog.filters.all') }}</button>
        <button
          v-for="cat in categories"
          :key="cat"
          :class="['chip', filters.category === cat && 'is-active']"
          @click="setFilter('category', cat)"
        >{{ t(`public.categories.${cat}`) }}</button>
      </div>
      <select class="select" :value="filters.sort || 'price_asc'" @change="(e) => setFilter('sort', e.target.value)">
        <option value="price_asc">{{ t('public.catalog.sort.price_asc') }}</option>
        <option value="price_desc">{{ t('public.catalog.sort.price_desc') }}</option>
        <option value="newest">{{ t('public.catalog.sort.newest') }}</option>
      </select>
    </div>

    <div v-if="vehicles.length === 0" class="empty">{{ t('public.catalog.empty') }}</div>

    <ul v-else class="grid">
      <li v-for="v in vehicles" :key="v.id" class="card">
        <a :href="`${props.base_path}/${v.id}`" class="card__link">
          <div class="card__photo">
            <img v-if="v.photo_url" :src="v.photo_url" :alt="`${v.make} ${v.model}`" loading="lazy" />
            <CatalogPlaceholder v-else kind="vehicle" :category="v.category" />
          </div>
          <div class="card__body">
            <p class="card__class">{{ t(`public.categories.${v.category}`) }}</p>
            <h3 class="card__title">{{ v.make }} {{ v.model }} <span v-if="v.year" class="card__year">{{ v.year }}</span></h3>
            <p v-if="cardMeta(v)" class="card__meta">{{ cardMeta(v) }}</p>
            <p class="card__price"><span class="fw-value">{{ v.daily_rate }}</span> <span class="card__per">{{ t('public.catalog.per_day') }}</span></p>
          </div>
        </a>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.catalog { max-width: 72rem; margin: 0 auto; padding: 2rem 1.5rem 4rem; width: 100%; }
.catalog__header { margin-bottom: 1.5rem; }
.catalog__title  { font-family: var(--font-display, var(--display)); font-size: clamp(1.75rem, 4vw, 2.25rem); margin: 0 0 0.25rem; color: var(--ink); font-weight: 700; }
.catalog__lede   { color: var(--inkSoft); margin: 0; }

.catalog__toolbar { display: flex; flex-wrap: wrap; gap: 0.75rem; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; }
.chips { display: flex; gap: 0.4rem; flex-wrap: wrap; }
.chip {
  padding: 0.4rem 0.9rem; border-radius: 999px; border: 1px solid var(--line); background: var(--surface);
  font-size: 0.875rem; color: var(--inkSoft); cursor: pointer;
}
.chip.is-active { background: var(--accent); color: var(--accentInk); border-color: var(--accent); }
.select {
  padding: 0.45rem 0.75rem; border-radius: 0.5rem; border: 1px solid var(--line);
  background: var(--surface); color: var(--ink); font-size: 0.875rem;
}

.empty { padding: 3rem 1rem; text-align: center; color: var(--muted); }

.grid { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr)); gap: 1rem; }

.card { background: var(--surface); border: 1px solid var(--line); border-radius: 0.75rem; overflow: hidden; transition: border-color 150ms ease, transform 150ms ease; }
.card:hover { border-color: color-mix(in oklch, var(--accent) 60%, var(--line)); transform: translateY(-1px); }
.card__link { display: block; color: inherit; text-decoration: none; }
.card__photo { aspect-ratio: 4 / 3; background: var(--surfaceAlt); display: grid; place-items: center; overflow: hidden; }
.card__photo img { width: 100%; height: 100%; object-fit: cover; }
.card__placeholder { font-size: 2rem; font-weight: 700; color: var(--muted); font-family: var(--font-display, var(--display)); letter-spacing: 0.05em; }
.card__body { padding: 0.875rem 1rem 1rem; }
.card__class { font-size: 0.75rem; color: var(--muted); text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 0.25rem; }
.card__title { font-size: 1rem; margin: 0 0 0.35rem; color: var(--ink); font-weight: 600; }
.card__year { color: var(--muted); font-weight: 400; margin-left: 0.15rem; }
.card__meta { margin: 0 0 0.5rem; font-size: 0.8125rem; color: var(--inkSoft); }
.card__price { margin: 0; font-size: 1.0625rem; color: var(--ink); }
.card__per { font-size: 0.8125rem; color: var(--muted); margin-left: 0.25rem; }
</style>
